import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import Link from 'next/link';
import { useEffect, useRef } from 'react';

import { getExchangeWinnersApi } from '@flipster/apis/v1/exchange';
import { OrderSideBadge } from '@flipster/features/src/misc/badge';
import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { OrderAttributionMapper, orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { Carousel, rollingProps } from '@flipster/react/design-system-legacy/carousel';
import Formatted from '@flipster/react/design-utils/formatted';
import { useIsElementInViewport } from '@flipster/react/misc/browser/intersection';
import { css } from '@flipster/styled-system/css';
import { flex, linkOverlay, stack } from '@flipster/styled-system/patterns';
import { breakpointMB } from '@flipster/utils/constants/breakpoint';
import segment from '@flipster/utils/segment';
import { EarnEvent, EarnEventDataType } from '@flipster/utils/segment/events/earn';

const datasetToSegmentData = (dataset: DOMStringMap) =>
  ({
    symbol: dataset.symbol!,
    side: dataset.side === 'Long' ? 'buy' : 'sell',
    realized_pnl: Number(dataset.realizedPnl),
    page_type: 'earn_page',
  }) satisfies EarnEventDataType[typeof EarnEvent.PnlBannerClick];

const ExchangeWinnersSection = () => {
  const alreadyViewed = useRef(false);

  const { instrumentsKeyBySymbol } = useSnapshotSpec();
  const [ref, isVisible] = useIsElementInViewport<HTMLDivElement>();

  const setOrderAttribution = useSetAtom(orderAttributionAtom);

  const { data: exchangeWinnersData } = useQuery({
    queryKey: ['getExchangeWinnersApi'],
    queryFn: getExchangeWinnersApi,
    select: (res) => res.data,
  });

  useEffect(() => {
    if (alreadyViewed.current) return;
    if (isVisible) {
      segment.track(EarnEvent.PnlBannerView);
      alreadyViewed.current = true;
    }
  }, [isVisible]);

  if (!exchangeWinnersData || !instrumentsKeyBySymbol) return null;

  const filteredExchangeWinnersData = exchangeWinnersData.winners.filter((w) => !!instrumentsKeyBySymbol[w.symbol]);

  /** filtered된 리스트가 3개 미만이면 배너를 보여주지 않음 */
  if (filteredExchangeWinnersData.length < 3) return null;

  return (
    <div ref={ref}>
      <Carousel
        slidesPerView={1}
        spaceBetween={16}
        breakpoints={{ [breakpointMB]: { slidesPerView: 3 } }}
        title={
          <>
            <h2 className={css({ textStyle: 'Title/S', color: 'content.default.level-4' })}>
              {exchangeWinnersData?.title}
            </h2>
            <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2', pt: 4 })}>
              Check out recent gainers.
            </p>
          </>
        }
        navigator={<Carousel.Navigator css={{ gridArea: { base: 'arrow-bottom', tablet: 'arrow-right' } }} />}
        css={{ rowGap: 16 }}
        {...rollingProps}
      >
        {/* filteredExchangeWinnersData가 3개일 경우 rolling되지 않는 이슈가 있음 */}
        {[...filteredExchangeWinnersData, ...filteredExchangeWinnersData].map((winner, i) => {
          const isSpot = Boolean(instrumentsKeyBySymbol?.[winner.symbol].isSpot);
          return (
            <Carousel.Slide css={{ position: 'relative' }} key={`${winner.nickname}_${winner.symbol}_${i + 1}`}>
              <div
                className={stack({
                  justify: 'space-between',
                  height: '82px',
                  py: 16,
                  px: 20,
                  bg: 'background.default.level-1',
                  rounded: 8,
                })}
              >
                <div className={flex({ width: '100%', justify: 'space-between', align: 'center' })}>
                  <SymbolDisplay
                    symbol={winner.symbol}
                    icon={<SymbolDisplay.Icon size={24} space={8} />}
                    mainLabel={
                      <SymbolDisplay.Label
                        value='shortName'
                        css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-4' }}
                      />
                    }
                  />
                  <OrderSideBadge orderSide={winner.side} />
                </div>
                <div className={flex({ width: '100%', justify: 'space-between', align: 'center', gap: 8 })}>
                  <p className={flex({ textStyle: 'Text/L/Regular', color: 'content.default.level-2', gap: 4 })}>
                    <span className={css({ lineClamp: 1, wordBreak: 'break-all' })}>{winner.nickname}</span>
                    <span className={css({ minW: '76px' })}>just gained</span>
                  </p>
                  <Formatted.Number value={winner.pnl} precision={2} withPlus withColor toFixed />
                </div>
              </div>
              <Link
                href={`/trade/${isSpot ? 'spot' : 'perpetual'}/${winner.symbol}`}
                onClick={(e) => {
                  setOrderAttribution(OrderAttributionMapper.earn.earnTraders);
                  segment.track(EarnEvent.PnlBannerClick, datasetToSegmentData(e.currentTarget.dataset));
                }}
                data-symbol={winner.symbol}
                data-side={winner.side}
                data-realized-pnl={winner.pnl}
                className={linkOverlay()}
                tabIndex={-1}
                aria-hidden
              />
            </Carousel.Slide>
          );
        })}
      </Carousel>
    </div>
  );
};

export default ExchangeWinnersSection;
